import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { money } from '../lib/format';

export interface GuestPayment {
  reference: string;
  amount: number;
  /** Null when the hotel takes no cards, or the gateway could not be reached. */
  gateway: { provider: string; keyId: string; orderId: string | null } | null;
}

interface RazorpayResult {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
  }
}

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Loads the gateway's own checkout script, once.
 *
 * It is Razorpay's script rather than a form of ours on purpose: card details
 * must never touch this page, and no gateway worth using would accept them
 * from anything else.
 */
function loadCheckout(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CHECKOUT_SRC}"]`);
    const script = existing ?? document.createElement('script');
    script.src = CHECKOUT_SRC;
    script.async = true;
    script.addEventListener('load', () => resolve(Boolean(window.Razorpay)));
    script.addEventListener('error', () => resolve(false));
    if (!existing) document.body.appendChild(script);
  });
}

/**
 * Paying, for a guest with no account.
 *
 * Renders nothing at all when there is nothing to pay into — a hotel that
 * collects at the desk is a perfectly ordinary hotel, and the guest should not
 * be shown a dead button. What settles the booking is the gateway's signed
 * callback checked on the server, never this component saying it went through.
 */
export function PayNow({
  payment,
  name,
  description,
  fallback,
  onPaid,
}: {
  payment: GuestPayment;
  /** The hotel, as the guest will see it on their card statement. */
  name: string;
  description: string;
  /** What to say when there is no way to pay online. */
  fallback: string;
  onPaid?: () => void;
}) {
  const [state, setState] = useState<'idle' | 'opening' | 'paid' | 'failed'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const gateway = payment.gateway;

  if (!gateway?.orderId) return <p className="small muted">{fallback}</p>;

  if (state === 'paid') {
    return <p className="small muted">Paid in full. Nothing further to settle.</p>;
  }

  const pay = async () => {
    setState('opening');
    setMessage(null);

    if (!(await loadCheckout())) {
      setState('failed');
      setMessage('The payment window could not be opened. You can still pay at the hotel.');
      return;
    }

    new window.Razorpay!({
      key: gateway.keyId,
      order_id: gateway.orderId,
      amount: Math.round(payment.amount * 100),
      currency: 'INR',
      name,
      description,
      handler: async (result: RazorpayResult) => {
        try {
          await api.post('/api/public/payments/confirm', {
            reference: payment.reference,
            providerRef: result.razorpay_payment_id,
            orderId: result.razorpay_order_id,
            signature: result.razorpay_signature,
          });
          setState('paid');
          onPaid?.();
        } catch (caught) {
          // The money may well have left the guest's account: never say it
          // failed outright, tell them what to quote.
          setState('failed');
          setMessage(
            caught instanceof ApiError
              ? caught.message
              : `We could not confirm that payment. Call the hotel and quote ${payment.reference}.`,
          );
        }
      },
      modal: { ondismiss: () => setState('idle') },
    }).open();
  };

  return (
    <>
      {message ? <p className="small warn">{message}</p> : null}
      <button className="btn primary" disabled={state === 'opening'} onClick={() => void pay()}>
        {state === 'opening' ? 'Opening…' : `Pay ${money(payment.amount)} now`}
      </button>
      <p className="small muted">{fallback}</p>
    </>
  );
}
